import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnimalNuisanceStore } from '@animal-nuisance/hooks/useAnimalNuisanceStore';
import { MapTemplate } from '@templates';
import { ErrorMessages, AssetType } from '@types';
import {
  addressResponseType,
  convertAddressToServiceLocation,
  convertServiceLocationToAddress,
  parseZodErrors,
  scrollTop,
  TextAreaMaxLengthSchema,
} from '@utils';
import { SelectedLocationTextContent } from './SelectedLocationTextContent';
import { LatLngTuple } from 'leaflet';
import { ZodError } from 'zod';
import { RoutesList } from '@animal-nuisance/routes/Routes';
import {
  additionalLocationDetails,
  locationContentData,
} from '../../../../constants/questionData';

export const IssueLocationPage = () => {
  const navigate = useNavigate();

  const {
    data: {
      serviceLocationDetails,
      isPhotoLocationStored,
      issueLocationMoreInfo,
    },
    update,
  } = useAnimalNuisanceStore();

  const { longitude, latitude } = serviceLocationDetails;

  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);

  const selectedAddress =
    latitude && longitude
      ? convertServiceLocationToAddress(serviceLocationDetails)
      : ({} as addressResponseType);
  const defaultLocation =
    selectedAddress?.latitude && selectedAddress?.longitude
      ? ([
          parseFloat(selectedAddress.latitude),
          parseFloat(selectedAddress.longitude),
        ] as LatLngTuple)
      : undefined;

  const errorPath = 'issue-Location';
  const assetType: AssetType = 'HighwaysCarriageways';

  const navigationIfInsideTfLBoundary = () => {
    setErrorMessages([]);
    navigate(RoutesList.REPORT_TO_TFL);
    scrollTop();
  };

  const handleOnNextClick = async () => {
    setErrorMessages([]);
    try {
      const { latitude, longitude } = serviceLocationDetails;
      if (!latitude || !longitude) {
        setErrorMessages([
          {
            message: 'Please select a location on the map',
            path: errorPath,
          },
        ]);
        return;
      }

      if (issueLocationMoreInfo) {
        TextAreaMaxLengthSchema({
          fieldNames: [additionalLocationDetails.id],
          maxLength: additionalLocationDetails.maxLength,
        }).parse({
          [additionalLocationDetails.id]: issueLocationMoreInfo,
        });
      }

      navigate(RoutesList.CONFIRMATION_EMAIL);
    } catch (error) {
      const validationErrors = parseZodErrors(error as ZodError);
      setErrorMessages(
        validationErrors.map((error) => ({
          message: error.message,
          path: error.path,
        })),
      );
    }
  };

  const handleOnPreviousClick = () => {
    navigate(RoutesList.UPLOAD_PHOTO);
    scrollTop();
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: handleOnPreviousClick,
  };

  const buttonNext = {
    label: 'Next',
    onClick: handleOnNextClick,
  };

  const handleAddressChange = (address: addressResponseType) => {
    setErrorMessages([]);
    const formattedAddress = convertAddressToServiceLocation(address);
    update('serviceLocationDetails', {
      ...serviceLocationDetails,
      ...formattedAddress,
    });
  };

  const pageTitle = isPhotoLocationStored
    ? locationContentData.titleWithLocation
    : locationContentData.titleWithoutLocation;
  const subText = isPhotoLocationStored
    ? locationContentData.descriptionWithLocation
    : locationContentData.descriptionWithoutLocation;

  const addressTitle = isPhotoLocationStored
    ? locationContentData.addressTitleWithLocation
    : locationContentData.addressTitleWithoutLocation;

  const additionalLocationDetailsWithOnChange = {
    ...additionalLocationDetails,
    isLabelTextTitle: false,
    name: additionalLocationDetails.id,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      update('issueLocationMoreInfo', e.target.value);
    },
    value: issueLocationMoreInfo || '',
    showCounter: true,
    maxLength: 500,
    errorMessages: errorMessages.filter(
      (error) => error.path === additionalLocationDetails.id,
    ),
  };

  return (
    <MapTemplate
      id={errorPath}
      labelText={pageTitle}
      subText={subText}
      errorPath={errorPath}
      errorMessages={errorMessages}
      onChangeAddress={handleAddressChange}
      defaultLocation={defaultLocation}
      selectedAddress={selectedAddress}
      customAddressContent={
        <SelectedLocationTextContent title={addressTitle} />
      }
      additionalLocationDetailsTextArea={additionalLocationDetailsWithOnChange}
      enableTfLBoundaryCheck={true}
      navigationIfInsideTfLBoundary={navigationIfInsideTfLBoundary}
      assetType={assetType}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
